import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * Shared in-flight / completed results keyed by token.
 * Survives React.StrictMode remounts so a single-use token is POSTed once,
 * and remounted UI still receives the deterministic success/error outcome.
 */
const verifyResults = new Map();

function formatVerifyError(err) {
  if (err?.response?.data?.detail) return err.response.data.detail;
  if (err?.code === "ECONNABORTED") return "Verification timed out. Please try again.";
  return "Could not verify this email.";
}

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { verifyEmail, isAuthenticated } = useAuth();
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("No verification token was found in this link.");
      return undefined;
    }

    let cancelled = false;

    const apply = (result) => {
      if (cancelled) return;
      setStatus(result.status);
      setMessage(result.message || "");
    };

    const existing = verifyResults.get(token);
    if (existing?.status === "success" || existing?.status === "error") {
      apply(existing);
      return undefined;
    }

    if (!existing) {
      const promise = verifyEmail(token)
        .then(() => {
          const result = { status: "success", message: "" };
          verifyResults.set(token, result);
          return result;
        })
        .catch((err) => {
          const result = { status: "error", message: formatVerifyError(err) };
          verifyResults.set(token, result);
          return result;
        });
      verifyResults.set(token, { status: "pending", promise });
      promise.then(apply);
    } else if (existing.promise) {
      existing.promise.then(apply);
    }

    // Do NOT abort the shared request on unmount — StrictMode remounts must
    // still observe the same in-flight promise. Local `cancelled` ignores stale setState.
    return () => {
      cancelled = true;
    };
  }, [token, verifyEmail]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="verify-email-page">
      <div className="max-w-md w-full text-center p-10 clip-frame border border-border bg-background">
        {status === "verifying" && (
          <div data-testid="verify-email-loading">
            <Loader2 size={36} className="mx-auto animate-spin text-[color:var(--gold)]" />
            <h1 className="display display-section text-2xl mt-6">Verifying your email…</h1>
          </div>
        )}
        {status === "success" && (
          <div data-testid="verify-email-success">
            <CheckCircle2 size={40} className="mx-auto text-[color:var(--teal)]" />
            <h1 className="display display-section text-3xl mt-6">Email verified.</h1>
            <p className="mt-3 text-steel-text">Your account is now fully activated.</p>
            <Link
              to={isAuthenticated ? "/portal" : "/login"}
              className="btn-gold clip-frame-sm px-6 py-3 text-sm uppercase tracking-widest font-mono-2 inline-flex items-center gap-2 mt-8"
              data-testid="verify-email-continue"
            >
              {isAuthenticated ? "Go to Portal" : "Sign in"}
            </Link>
          </div>
        )}
        {status === "error" && (
          <div data-testid="verify-email-error">
            <XCircle size={40} className="mx-auto text-destructive" />
            <h1 className="display display-section text-3xl mt-6">Verification failed.</h1>
            <p className="mt-3 text-steel-text">{message}</p>
            <Link to="/login" className="text-[color:var(--gold)] underline text-sm mt-6 inline-block">Back to Sign in</Link>
          </div>
        )}
      </div>
    </div>
  );
}
